import { useEffect, useMemo, useState } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import "./InviteEarn.css";
import {
  REFERRAL_EXAMPLE_COPY,
  REFERRAL_FRIEND_BONUS_PERCENT,
  REFERRAL_INVITER_PERCENT_BY_PLAN,
  REFERRAL_PAGE_SUBTITLE,
  REFERRAL_PLAN_LABELS,
  REFERRAL_RATE_EXPLAINER,
  REFERRAL_SCOPE_NOTE,
  buildInviteUrl,
  getInviterPercent,
} from "../../config/app/referral";
import { buildChannelSharePresets } from "../../config/app/referralShareCopy";
import { getReferralMe } from "../../services/api/app/referral";
import { copyTextToClipboard } from "../../services/api/editor/share";
import { getUserDisplayName } from "../../utils/displayName";
import {
  twitterIcon,
  youtubeIcon,
  instaIcon,
} from "../../icons/assets";
import InviteAvatar from "./InviteAvatar";
import InviteStatusTrack from "./InviteStatusTrack";
import InviteShareModal from "./InviteShareModal";
import { INVITE_STATUS_STEPS } from "./inviteMockData";

const PLAN_ORDER = ["free", "lite", "pro", "max"];

const EMPTY_REFERRAL_ME = {
  referral_code: "",
  plan_key: "free",
  stats: {
    invited: 0,
    signed_up: 0,
    welcome_claimed: 0,
    subscribed: 0,
    rewarded: 0,
    credits_lifetime: 0,
    credits_year: 0,
    credits_month: 0,
  },
};

const HOW_IT_WORKS = [
  {
    title: "Invited",
    body: "Friend opens your link or enters your code.",
  },
  {
    title: "Signed up",
    body: "They create a LazyStudio account with your referral.",
  },
  {
    title: "Welcome gifts",
    body: "They claim welcome credits and start trying tools.",
  },
  {
    title: "Subscribed",
    body: "They buy their first subscription plan.",
  },
  {
    title: "Rewarded",
    body: "You both get credits — their +20% bonus, your plan rate.",
  },
];

const CHANNEL_ICONS = {
  twitter: twitterIcon,
  instagram: instaIcon,
  youtube: youtubeIcon,
};

const CHANNEL_ORDER = [
  "whatsapp",
  "instagram",
  "twitter",
  "youtube",
  "email",
  "telegram",
  "sms",
];

const InviteEarn = () => {
  const { user } = useUser();
  const { getToken, isSignedIn } = useAuth();
  const [me, setMe] = useState(EMPTY_REFERRAL_ME);
  const [invites, setInvites] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [copyState, setCopyState] = useState("idle");
  const [copyTarget, setCopyTarget] = useState(null);
  const [shareHint, setShareHint] = useState("");
  const [activeShareId, setActiveShareId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSignedIn) return;
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const data = await getReferralMe(token);
        if (cancelled || !data) return;
        setMe({
          referral_code: data.referral_code || "",
          plan_key: data.plan_key || "free",
          stats: {
            invited: data.stats?.invited ?? 0,
            signed_up: data.stats?.signed_up ?? 0,
            welcome_claimed: data.stats?.welcome_claimed ?? 0,
            subscribed: data.stats?.subscribed ?? 0,
            rewarded: data.stats?.rewarded ?? 0,
            credits_lifetime: data.stats?.credits_lifetime ?? 0,
            credits_year: data.stats?.credits_year ?? 0,
            credits_month: data.stats?.credits_month ?? 0,
          },
        });
        setInvites(Array.isArray(data.invites) ? data.invites : []);
        setLoadError("");
      } catch {
        if (!cancelled) {
          setMe(EMPTY_REFERRAL_ME);
          setInvites([]);
          setLoadError("Could not load referral data. Try refreshing.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn]);

  const inviteUrl = useMemo(
    () => buildInviteUrl(me.referral_code, "/"),
    [me.referral_code]
  );
  const inviterPercent = getInviterPercent(me.plan_key);
  const planLabel = REFERRAL_PLAN_LABELS[me.plan_key] || "Free";
  const inviterName = getUserDisplayName(user) || "You";
  const inviterAvatar = user?.imageUrl || null;

  const sharePresets = useMemo(
    () =>
      buildChannelSharePresets({
        inviteUrl,
        inviterName,
        planKey: me.plan_key,
        toolLabel: "General invite",
        toolBlurb: "LazyStudio",
      }),
    [inviteUrl, inviterName, me.plan_key]
  );

  const shareChannels = CHANNEL_ORDER.map((id) => sharePresets[id]).filter(
    Boolean
  );

  const planIndex = Math.max(0, PLAN_ORDER.indexOf(me.plan_key));

  const funnel = [
    { key: "invited", label: "Invited", count: me.stats.invited },
    { key: "signed_up", label: "Signed up", count: me.stats.signed_up },
    {
      key: "welcome_claimed",
      label: "Welcome gifts",
      count: me.stats.welcome_claimed,
    },
    { key: "subscribed", label: "Subscribed", count: me.stats.subscribed },
    { key: "rewarded", label: "Rewarded", count: me.stats.rewarded },
  ];

  const flashHint = (message) => {
    setShareHint(message);
    window.setTimeout(() => setShareHint(""), 2200);
  };

  const copyText = async (text, hint = "Copied", target = null) => {
    // Clipboard API needs HTTPS; fall back for http:// IP deploys
    const ok = await copyTextToClipboard(text);
    setCopyTarget(target);
    if (ok) {
      setCopyState("copied");
      flashHint(hint);
    } else {
      setCopyState("error");
      flashHint("Copy failed");
    }
    window.setTimeout(() => {
      setCopyState("idle");
      setCopyTarget(null);
    }, 1800);
  };

  return (
    <div className="invite-earn">
      <header className="invite-earn__header">
        <div className="invite-earn__header-row">
          <InviteAvatar
            src={inviterAvatar}
            name={inviterName}
            size={60}
            className="invite-earn__you-avatar"
            alt=""
          />
          <div>
            <h1>Invite & Earn</h1>
            <p className="invite-earn__subtitle">{REFERRAL_PAGE_SUBTITLE}</p>
            <p className="invite-earn__scope">{REFERRAL_SCOPE_NOTE}</p>
            {loadError ? (
              <p className="invite-earn__scope" role="status">
                {loadError}
              </p>
            ) : null}
          </div>
        </div>
      </header>

      <main className="invite-earn__main">
        <div className="invite-earn__body">
          <div className="invite-earn__col invite-earn__col--main">
            <article className="invite-card invite-card--reward">
              <div className="invite-card__head">
                <h2 className="invite-card__title">Your reward rate & earnings</h2>
                <InviteAvatar
                  src={inviterAvatar}
                  name={inviterName}
                  size={36}
                  alt=""
                />
              </div>

              <div className="invite-rate__split" aria-label="You and friend rewards">
                <div className="invite-rate__split-card">
                  <p className="invite-rate__split-label">You get</p>
                  <p className="invite-rate__split-value">
                    <span>{inviterPercent}%</span>
                  </p>
                  <p className="invite-rate__split-meta">
                    of their first-plan credits · {planLabel} plan
                  </p>
                </div>
                <div className="invite-rate__split-card">
                  <p className="invite-rate__split-label">They get</p>
                  <p className="invite-rate__split-value">
                    <span>+{REFERRAL_FRIEND_BONUS_PERCENT}%</span>
                  </p>
                  <p className="invite-rate__split-meta">
                    extra credits on their first subscription
                  </p>
                </div>
              </div>

              <div className="invite-rate__tiers-block">
                <div className="invite-rate__tiers-head">
                  <h3 className="invite-rate__tiers-title">Your earn rate by plan</h3>
                  <p className="invite-rate__tiers-explainer">
                    {REFERRAL_RATE_EXPLAINER}
                  </p>
                </div>
                <ul className="invite-rate__tiers" aria-label="Earn rate by your plan">
                  {PLAN_ORDER.map((key, index) => {
                    const pct = REFERRAL_INVITER_PERCENT_BY_PLAN[key];
                    const active = key === me.plan_key;
                    const lower = index < planIndex;
                    return (
                      <li
                        key={key}
                        className={[
                          "invite-rate__tier",
                          active ? "is-active" : "",
                          lower ? "is-passed" : "",
                          !active && !lower ? "is-higher" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <span className="invite-rate__tier-name">
                          {REFERRAL_PLAN_LABELS[key]}
                        </span>
                        <span className="invite-rate__tier-pct">{pct}%</span>
                        <span className="invite-rate__tier-state">
                          {active ? "Your plan" : "\u00a0"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <p className="invite-rate__example">{REFERRAL_EXAMPLE_COPY}</p>
              </div>

              <div className="invite-rate__stats-wrap">
                <p className="invite-rate__stats-caption">Credits earned</p>
                <div className="invite-rate__stats" aria-label="Credits earned by period">
                  <div>
                    <span className="invite-rate__stat-value">
                      {me.stats.credits_lifetime}
                    </span>
                    <span className="invite-rate__stat-label">Lifetime</span>
                  </div>
                  <div>
                    <span className="invite-rate__stat-value">
                      {me.stats.credits_year}
                    </span>
                    <span className="invite-rate__stat-label">This year</span>
                  </div>
                  <div>
                    <span className="invite-rate__stat-value">
                      {me.stats.credits_month}
                    </span>
                    <span className="invite-rate__stat-label">This month</span>
                  </div>
                </div>
              </div>
            </article>

            <section className="invite-section invite-section--stats">
              <h2 className="invite-section__title">Invitee stats</h2>
              <ol className="invite-funnel invite-funnel--spread">
                {funnel.map((step, index) => (
                  <li key={step.key} className="invite-funnel__step">
                    <div className="invite-funnel__count">{step.count}</div>
                    <div className="invite-funnel__label">{step.label}</div>
                    {index < funnel.length - 1 && (
                      <span className="invite-funnel__arrow" aria-hidden="true">
                        →
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </section>

            <section className="invite-section invite-section--people">
              <div className="invite-section__head">
                <h2 className="invite-section__title">People you invited</h2>
                <p className="invite-section__hint">
                  Rewards unlock when a friend starts their first subscription.
                </p>
              </div>

              <ul className="invite-people">
                {invites.length === 0 ? (
                  <li className="invite-person invite-person--empty">
                    No invites yet. Share your link to get started.
                  </li>
                ) : null}
                {invites.map((row) => (
                  <li key={row.id} className="invite-person">
                    <div className="invite-person__avatars">
                      <InviteAvatar
                        src={inviterAvatar}
                        name={inviterName}
                        size={28}
                        className="invite-person__inviter"
                        alt=""
                      />
                      <InviteAvatar
                        src={row.avatar_url}
                        name={row.friend_name}
                        size={36}
                        className="invite-person__invitee"
                        alt=""
                      />
                    </div>

                    <div className="invite-friend">
                      <span className="invite-friend__name">
                        {row.friend_name}
                      </span>
                      <span className="invite-friend__email">
                        {row.friend_email}
                      </span>
                    </div>

                    <div className="invite-person__reward">
                      <span className="invite-person__credits">
                        {row.credits_earned != null
                          ? `+${row.credits_earned} credits`
                          : "—"}
                      </span>
                      <span className="invite-person__plan">
                        {row.plan_label || "—"}
                      </span>
                      <span className="invite-person__date">
                        {row.date
                          ? new Date(row.date).toLocaleString(undefined, {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })
                          : "—"}
                      </span>
                    </div>

                    <div className="invite-person__track">
                      <InviteStatusTrack status={row.status} />
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="invite-earn__col invite-earn__col--side">
            <div className="invite-earn__side-sticky">
              <article className="invite-card invite-card--invite">
                <div className="invite-card__head">
                  <div className="invite-card__title-row">
                    <h2 className="invite-card__title">Your invite</h2>
                    <span
                      className={`invite-card__copy-hint${shareHint ? " is-visible" : ""}`}
                      role="status"
                      aria-live="polite"
                    >
                      {shareHint || "\u00a0"}
                    </span>
                  </div>
                  <div className="invite-pair" aria-label="You invite friends">
                    <InviteAvatar
                      src={inviterAvatar}
                      name={inviterName}
                      size={32}
                      alt=""
                    />
                    <span className="invite-pair__connector" aria-hidden="true">
                      →
                    </span>
                    <div className="invite-pair__stack" aria-hidden="true">
                      {invites.slice(0, 3).map((row) => (
                        <InviteAvatar
                          key={row.id}
                          src={row.avatar_url}
                          name={row.friend_name}
                          size={26}
                          className="invite-pair__stack-item"
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="invite-field">
                  <span className="invite-field__label">Invite code</span>
                  <div className="invite-field__row">
                    <code className="invite-code">{me.referral_code}</code>
                    <button
                      type="button"
                      className="invite-btn-secondary invite-btn-copy"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        copyText(me.referral_code, "Code copied", "code");
                      }}
                    >
                      {copyState === "copied" && copyTarget === "code"
                        ? "Copied"
                        : "Copy"}
                    </button>
                  </div>
                </div>
                <div className="invite-field">
                  <span className="invite-field__label">Invite link</span>
                  <div className="invite-field__row">
                    <input
                      className="invite-link-input"
                      value={inviteUrl}
                      readOnly
                      aria-label="Invite link"
                    />
                    <button
                      type="button"
                      className="invite-btn-secondary invite-btn-copy"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        copyText(inviteUrl, "Link copied", "link");
                      }}
                    >
                      {copyState === "copied" && copyTarget === "link"
                        ? "Copied"
                        : copyState === "error" && copyTarget === "link"
                          ? "Copy failed"
                          : "Copy link"}
                    </button>
                  </div>
                </div>

                <div className="invite-share">
                  <span className="invite-share__label">Share via</span>
                  <div className="invite-share__channels">
                    {shareChannels.map((channel) => (
                      <button
                        key={channel.id}
                        type="button"
                        className="invite-share__btn"
                        onClick={() => setActiveShareId(channel.id)}
                        title={channel.label}
                        aria-label={`Share via ${channel.label}`}
                      >
                        {CHANNEL_ICONS[channel.id] ? (
                          <img src={CHANNEL_ICONS[channel.id]} alt="" />
                        ) : (
                          <span className="invite-share__mark">
                            {channel.mark}
                          </span>
                        )}
                        <span className="invite-share__name">{channel.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </article>

              <article className="invite-card invite-card--howto">
                <h2 className="invite-card__title">How invites progress</h2>
                <p className="invite-howto__intro">
                  {INVITE_STATUS_STEPS.map((s) => s.label).join(" → ")}
                </p>
                <ol className="invite-howto">
                  {HOW_IT_WORKS.map((step, index) => (
                    <li key={step.title} className="invite-howto__item">
                      <span className="invite-howto__num">{index + 1}</span>
                      <div>
                        <div className="invite-howto__title">{step.title}</div>
                        <p className="invite-howto__body">{step.body}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </article>
            </div>
          </aside>
        </div>
      </main>

      {activeShareId ? (
        <InviteShareModal
          channelId={activeShareId}
          icon={CHANNEL_ICONS[activeShareId]}
          referralCode={me.referral_code}
          inviterName={inviterName}
          planKey={me.plan_key}
          initialLandingId="remove-background"
          onClose={() => setActiveShareId(null)}
          onShared={(id) =>
            flashHint(`Shared via ${sharePresets[id]?.label || id}`)
          }
        />
      ) : null}
    </div>
  );
};

export default InviteEarn;
